<template>
	<div
		class="accessible-item"
		:data-ally-item="name"
		:tabindex="myTabIndex"
		@keydown="onKeydown"
		@focus="onFocus"
		@blur="onBlur"
	>
		<slot />
	</div>
</template>

<script>
import { highlightFocus } from '@/helperMethods'
import { createLogger } from '@/helpers/log'
import { sleep } from '@/helpers/utils'
import router from '@/router'
import isEqual from 'lodash/isEqual'
import scrollIntoView from 'scroll-into-view-if-needed'

export const log = createLogger('Accessible')

export const INVERT = 'invert'

const getAccessibleQuerySelector = (name) => `[data-ally-item=${name}]`

export default {
	name: 'Accessible',
	props: {
		name: { type: String, required: true },
		arrows: [Boolean, String],
		autoFocus: { type: Boolean, default: null },
		trapped: { type: Boolean, default: null },
		rootSelector: String,
		focusFirstItem: { type: Boolean, default: null },
		trapOn: { type: String, default: 'Enter' },
		tabOrder: { type: Array, default: () => [] },
		wait: { type: Number, default: 0 },
	},

	data() {
		return {
			myTabIndex: 0,
			tabList: this.tabOrder,
			currentIndex: -1,
			internalNav: false,
			hasActiveElement: false,
		}
	},

	emits: ['blurOut', 'focus', 'blur', 'enter', 'esc', 'tab', 'tablist'],
	expose: ['focusMe'],

	inject: {
		sectionName: {
			from: 'sectionName',
			default: null,
		},
		moveToNextSection: {
			from: 'moveToNextSection',
			default: null,
		},
		moveToPrevSection: {
			from: 'moveToPrevSection',
			default: null,
		},
		moveToSectionByName: {
			from: 'moveToSectionByName',
			default: null,
		},
	},

	computed: {
		isInverted() {
			return this.arrows === INVERT
		},

		isLast() {
			return this.currentIndex === this.tabList.length - 1
		},

		isTrapped() {
			return this.trapped || this.internalNav
		},

		root() {
			// this component's container reference element
			if (this.rootSelector) {
				const elem = this.$el.querySelector(this.rootSelector)
				return elem || this.$el
			}
			return this.$el
		},
	},

	watch: {
		tabOrder: {
			handler(tabOrder, prev) {
				if (isEqual(tabOrder, prev)) return
				this.log('setting tab list from prop change')
				this.setTabList(tabOrder)
			},
			deep: true,
		},
	},

	async mounted() {
		this.setTabList(this.tabOrder)

		if (this.autoFocus) {
			this.log('auto focusing')
			await this.$nextTick()
			if (this.wait) await this.sleep(this.wait)
			this.focusMe()
		}

		this.routerHook = router.afterEach((to, from) => {
			if (to.name !== from.name) {
				// if navigating to a new route, reset the tab order
				this.setTabList(this.tabOrder)
			}
		})

		this.$el.addEventListener('focusin', this.onFocusIn)
		this.$el.addEventListener('focusout', this.onFocusOut)
		document.addEventListener('keydown', this.onDocumentTab)
	},

	beforeUnmount() {
		this.routerHook?.()
		document.removeEventListener('keydown', this.onDocumentTab)
		this.$el.removeEventListener('focusin', this.onFocusIn)
		this.$el.removeEventListener('focusout', this.onFocusOut)
	},

	methods: {
		sleep,

		log(...args) {
			if (!this.accessibilityMode) return
			log(this.name, ...args)
		},

		onKeydown(e) {
			if (!this.preventIfIAmTarget(e)) return
			// we only want to handle these events if we were the direct target
			switch (e.key) {
				case 'Escape':
					return this.onEsc(e)
				case 'Enter':
					return this.onEnter(e)
				case 'Tab':
					return this.onTab(e)
				case 'ArrowUp':
					if (!this.arrows) return
					return this.focusOnSiblingAccessible(this.isInverted ? 1 : -1)
				case 'ArrowDown':
					if (!this.arrows) return
					return this.focusOnSiblingAccessible(this.isInverted ? -1 : 1)
			}
		},

		onFocus(e) {
			if (this.preventIfIAmTarget(e)) {
				this.$emit('focus', e)
			}
		},

		onBlur(e) {
			if (this.preventIfIAmTarget(e)) {
				this.setTabList(this.tabOrder)
				this.$emit('blur', e)
			}
		},

		onEnter(e) {
			if (this.trapOn === 'Enter') {
				this.moveToNextElem()
				this.internalNav = true
				this.currentIndex = 0
			}
			this.$emit('enter', e)
		},

		onEsc(e) {
			this.$emit('esc', e)
			const { allyItem, allySection } = e.target.dataset
			if (allyItem === allySection) return
			this.jumpToSectionByName(this.sectionName)
		},

		onTab(e) {
			this.accessibilityMode = true
			const { allyItem, allySection } = e.target.dataset
			allyItem === allySection && (this.internalNav = false)

			if (this.trapOn === 'Tab') {
				this.moveToNextElem()
				this.internalNav = true
			}

			if (!this.isTrapped) {
				if (e.shiftKey) {
					this.jumpToPrevSection()
				} else {
					this.jumpToNextSection()
				}
			}

			this.$emit('tab', e)
		},

		preventIfIAmTarget(e, elem = this.root) {
			const keys = ['Escape', 'Enter', 'Tab', 'ArrowUp', 'ArrowDown']
			if (
				elem === e.target &&
				keys.includes(e.key) &&
				(elem.tagName !== 'SELECT' || e.key === 'Tab')
			) {
				if (e.key === 'Escape') {
					this.accessibilityMode = false
					this.setTabList(this.tabOrder)
				} else {
					e.preventDefault()
					e.stopPropagation()
				}
				return true
			}
			return false
		},

		getAllSiblings() {
			const sibs = document.querySelectorAll(
				getAccessibleQuerySelector(this.name)
			)
			return Array.from(sibs)
		},

		focusOnSiblingAccessible(indexOffset) {
			const allSiblings = this.getAllSiblings()
			const myIndex = allSiblings.indexOf(this.$el)
			const goToIndex = myIndex + indexOffset
			const sibling = allSiblings[goToIndex]
			this.focusOnElem(sibling)
		},

		async focusMe() {
			this.log('focusMe()')
			if (this.currentIndex >= 0) {
				this.log('focusMe(): focusing on existing index', this.currentIndex)
				this.focusOnItem(this.tabList[this.currentIndex])
			} else if (this.focusFirstItem) {
				this.log('focusMe(): focusing on first item', 0)
				await this.setTabList(this.tabOrder)
				this.currentIndex = 0
				this.focusOnItem(this.tabList[0])
			} else {
				await this.setTabList(this.tabOrder)
				this.focusOnElem(this.root)
			}
		},

		async moveToAccessibleByName(name) {
			const selector = getAccessibleQuerySelector(name)
			this.log('moving to accessible', name, selector)
			const elem = document.querySelector(selector)
			if (elem.__vnode?.ctx?.exposed?.focusMe) {
				elem.__vnode.ctx.exposed.focusMe()
			} else if (elem?.__vnode?.focusMe) {
				elem.__vnode.focusMe
			} else {
				this.log(`cannot move to accessible "${name}"`, elem?.__vnode)
			}
		},

		async setTabList(array) {
			const waitItem = array.find((item) => item.wait)
			if (waitItem) {
				await sleep(waitItem.wait)
			}
			this.tabList = array.map(this.mapTabOrderItem.bind(this)).flat()
			this.currentIndex = -1
			this.hasActiveElement = false
			this.$emit('tablist', this.tabList)
		},

		mapTabOrderItem(item) {
			if (item.all) {
				const elemList = this.root.querySelectorAll(item.all)

				const mapped = Array.from(elemList).map((elem) => ({
					Tab: item.Tab,
					Enter: item.Enter,
					Escape: item.Escape,
					ArrowUp: item.ArrowUp,
					ArrowDown: item.ArrowDown,
					preventNextOnTab: item.preventNextOnTab,
					clickOnEnter: item.clickOnEnter,
					arrows: item.arrows,
					elem,
					css: item.all,
					fromAll: true,
				}))

				if (item.firstOfAll && mapped[0]) {
					mapped[0].startAtIndex = mapped.findIndex((x) =>
						x.elem.matches(item.firstOfAll)
					)
				}

				return mapped
			} else if (item.name) {
				item.elem = document.querySelector(
					getAccessibleQuerySelector(item.name)
				)
			}

			return item
		},

		moveToNextElem({ currCount = 0, direction = 1 } = {}) {
			if (currCount > 10) {
				// just to prevent infinite recursive call
				this.log('moveToNextElem(): looped up to 10 times, now bailing...')
				return
			}

			const prevIndex = this.currentIndex
			const prevItem = this.tabList[prevIndex]
			this.currentIndex += direction

			// moving forward
			if (this.currentIndex >= this.tabList.length) {
				this.log('moveToNextElem(): looping around to start')
				this.currentIndex = 0
			}

			// moving backward
			if (this.currentIndex <= -1) {
				this.log('moveToNextElem(): looping around to end')
				this.currentIndex = this.tabList.length - 1
			}

			let nextItem = this.tabList[this.currentIndex]

			if (nextItem?.startAtIndex) {
				this.currentIndex += nextItem.startAtIndex
				nextItem.startAtIndex = false
				nextItem = this.tabList[this.currentIndex]
			}

			const worked = this.focusOnItem(nextItem)
			if (!worked && this.currentIndex < this.tabList.length) {
				this.log(
					`could not focus on next item at ${this.currentIndex}, moving to next...`,
					nextItem
				)
				// if we couldn't find this element and we're still not to the end
				// of the list, then try to find the next one
				this.moveToNextElem({ currCount: currCount + 1, direction: direction })
			}
		},

		async focusOnElem(elem) {
			if (!elem || !this.accessibilityMode) return false
			await sleep(100)
			elem.tabIndex = 0
			elem.focus()
			highlightFocus(elem)
			this.log('focused element:', elem)
			const onElemBlur = () => {
				elem.tabIndex = -1
				elem.removeEventListener('blur', onElemBlur)
			}
			elem.addEventListener('blur', onElemBlur)
		},

		focusOnItem(item) {
			if (!item) return false
			const elem =
				item.elem && item.elem.isConnected
					? item.elem
					: this.$el.querySelector(item.css)

			if (!elem) {
				this.log('could not find element:', item.elem || item.css)
				return false
			}
			scrollIntoView(elem, {
				scrollMode: 'if-needed',
				block: 'nearest',
				inline: 'nearest',
			})

			// Can't clean this up by putting it up in
			// the `if (!elem) return false` line because
			// we have an issue if scrollIntoView() is
			// after the following isVisible() line
			//
			if (!item.force && !this.isVisible(elem)) {
				this.log('element is not visible', elem)
				return false
			}
			item.elem = elem
			this.focusOnElem(elem)
			this.setupFocusedItem(item)
			// if (item.selectText) {
			// 	elem.select()
			// }
			return true
		},

		isVisible(elem) {
			if (this.trapped) return true
			if (!(elem instanceof Element)) return

			const rect = elem.getBoundingClientRect()
			const style = getComputedStyle(elem)
			if (style.display === 'none') return false
			if (style.visibility !== 'visible') return false
			if (style.opacity < 0.1) return false
			if (
				elem.offsetWidth + elem.offsetHeight + rect.height + rect.width ===
				0
			) {
				return false
			}

			const width =
				elem.tagName === 'svg' ? elem.width.baseVal.value : elem.offsetWidth
			const height =
				elem.tagName === 'svg' ? elem.height.baseVal.value : elem.offsetHeight
			const winWidth = document.documentElement.clientWidth || window.innerWidth

			const elemCenter = {
				x: rect.left + width / 2,
				y: rect.top + height / 2,
			}

			if (elemCenter.x < 0 || elemCenter.y < 0) {
				this.log('elem center is negative', elemCenter)
				return false
			}

			if (elemCenter.x > winWidth || elemCenter.y > winWidth) {
				this.log('elem center off screen', elemCenter, winWidth)
				return false
			}

			let pointContainer = document.elementFromPoint(elemCenter.x, elemCenter.y)

			do {
				if (
					pointContainer === elem ||
					pointContainer.className === 'accessibility-highlight-border'
				)
					return true
			} while ((pointContainer = pointContainer.parentNode))
			return false
		},

		setupFocusedItem(item) {
			if (!item || !item.elem) return false
			const { elem } = item
			if (item.clickOnEnter) {
				this.augmentItemEventHandler(item, 'Enter', (vm, item, e) => {
					this.clickOnElem(item.elem)
				})
			}

			this.augmentItemEventHandler(item, 'Tab', (vm, item, e) => {
				this.accessibilityMode = true

				if (this.isTrapped && !item.preventNextOnTab) {
					if (e.shiftKey) {
						this.moveToNextElem({ direction: -1 })
					} else {
						this.moveToNextElem()
					}
				} else if (this.isLast) {
					if (e.shiftKey) {
						this.jumpToPrevSection()
					} else {
						this.jumpToNextSection()
					}
				}
			})

			this.augmentItemEventHandler(item, 'Escape', (vm, item, e) => {
				if (this.internalNav) {
					e.stopPropagation()
					this.internalNav = false
					this.focusMe()
				}
			})

			if (item.arrows) {
				this.augmentItemEventHandler(item, 'ArrowDown', () => {
					this.moveToNextElem()
				})
				this.augmentItemEventHandler(item, 'ArrowUp', () => {
					this.moveToNextElem({ direction: -1 })
				})
			}

			const onKeydown = (e) => this.onItemKeydown(e, item)
			const onBlur = () => {
				elem.removeEventListener('keydown', onKeydown)
				elem.removeEventListener('blur', onBlur)
			}

			elem.addEventListener('keydown', onKeydown)
			elem.addEventListener('blur', onBlur)
		},

		augmentItemEventHandler(item, eventName, fn) {
			if (item[eventName]?._augmented) return
			const origFn = item[eventName]
			item[eventName] = (vm, item, e) => {
				this.preventIfIAmTarget(e, item.elem)
				fn(vm, item, e, this)
				return origFn?.(vm, item, e, this) ?? undefined
			}
			item[eventName]._augmented = true
		},

		async onItemKeydown(e, item) {
			this.preventIfIAmTarget(e, item.elem)
			const result = await this.callItemHandler(item, e)
			this.handleEventResult(item, result)
		},

		getVueForElem(elem) {
			if (!elem) return null
			while (!elem.__vnode && elem.parentElement) {
				elem = elem.parentElement
			}
			return elem.__vnode
		},

		callItemHandler(item, e, eventName = e.key) {
			if (!item[eventName] || item.elem !== e.target) return false
			const vm = this.getVueForElem(item.elem)
			return item[eventName](vm, item, e)
		},

		clickOnElem(elem) {
			if (!elem) {
				this.log('tried to click elem, but none provided', elem)
				return false
			}
			if (elem.dispatchEvent) {
				const clickEvent = new CustomEvent('click', { bubbles: true })
				this.log('dispatching element click', clickEvent, elem)
				elem.dispatchEvent(clickEvent)
			} else if (elem.click) {
				this.log('clicking element', `"${elem.textContent}"`, elem)
				elem.click()
			} else {
				this.log('could not click elem', elem)
			}
		},

		async handleEventResult(item, result) {
			if (!item) return
			if (Array.isArray(result)) {
				// if the handler returned an array, assume this is a nested tab list
				await this.$nextTick()
				this.setTabList(result)
				this.internalNav = true
				if (item.stayOnFocus) return
				this.moveToNextElem()
				this.internalNav = true
				return
			}
			if (result === null) {
				// if handler returned null, reset back to the original tabOrder
				this.setTabList(this.tabOrder)
				this.focusMe()
				return
			}
		},

		jumpToNextSection() {
			this.setTabList(this.tabOrder)
			this.moveToNextSection(this.sectionName)
		},

		jumpToPrevSection() {
			this.setTabList(this.tabOrder)
			this.moveToPrevSection(this.sectionName)
		},

		jumpToSectionByName(sectionName) {
			this.setTabList(this.tabOrder)
			this.moveToSectionByName(sectionName)
		},

		onFocusIn() {
			clearTimeout(this.focusOutTimeout)
			this.hasActiveElement = true
		},

		onFocusOut() {
			if (this.hasActiveElement) {
				// 'focusout' will get fired every 'keydown' event, followed
				// immediately by another 'focusin' again, so this avoids the flag
				// changing again rapidly by adding a delay
				this.focusOutTimeout = setTimeout(this.onFocusLeave, 100)
			}
		},

		onFocusLeave() {
			this.hasActiveElement = false
			if (!this.internalNav) {
		this.setTabList(this.tabOrder)
	}
			// this.setTabList(this.tabOrder)
		},

		onDocumentTab(e) {
			if (e.key !== 'Tab') return
			if (this.hasActiveElement) {
				this.focusMe()
			}
		},

		async requeryTabList() {
			const currIndex = this.currentIndex
			this.setTabList(this.tabOrder)
			await this.$nextTick()
			this.currentIndex = currIndex - 1
			this.moveToNextElem()
		},
	},
}
</script>

<style lang="scss" scoped></style>
